/**
 * VP-222 — blok „Sklep" na górze zakładki Przegląd w trybie e-commerce.
 * KPI sklepu + trzy listy: Produkty (stan magazynu), Zamówienia, Zwroty.
 *
 * Renderuje się tylko przy podłączonym Shopify; brak integracji to pusty stan
 * z linkiem do /integracje, nie błąd. Okno czasu bierzemy z selektora dat
 * dashboardu (ten sam preset/zakres, więc kafle zgadzają się z resztą widoku).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { formatCurrency } from "@/lib/currency";
import DashboardMetricCard from "@/components/ui/dashboard-overview";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Package,
  Receipt,
  RotateCcw,
  Search,
  ShoppingBag,
  Store,
  Wallet,
} from "lucide-react";

type ShopSubView = "products" | "orders" | "refunds";

type ShopProductRow = {
  id: number;
  title: string;
  sku: string;
  price: string;
  inventory: number;
  status: string;
  variantCount: number;
};

type ShopProductsResponse = {
  connected: boolean;
  shopName?: string;
  products?: ShopProductRow[];
  activeProducts?: number;
  lowStockThreshold?: number;
};

type ShopOrderRow = {
  id: number;
  name: string;
  createdAt: string;
  customer: string | null;
  total: number;
  currency: string;
  financialStatus: string;
  cancelled: boolean;
  itemCount: number;
};

type ShopOrdersResponse = {
  connected: boolean;
  orders?: ShopOrderRow[];
  netRevenue?: number;
  liveOrders?: number;
  avgOrderValue?: number;
  currency?: string;
  visibleSince?: string | null;
};

type ShopRefundRow = {
  id: string;
  orderId: string | null;
  refundedAt: string | null;
  amount: number;
  currency: string | null;
  reason: string;
};

type ShopSectionProps = {
  datePreset: string;
  customRange?: { since: string; until: string };
  forClientUserId?: string;
};

const PAGE_SIZE = 10;
const STALE_TIME_MS = 5 * 60 * 1000;
const DEFAULT_CURRENCY = "PLN";

const SUB_VIEW_CONFIG: Record<ShopSubView, { labelKey: string; icon: typeof Package }> = {
  products: { labelKey: "dashboard.shop.tabs.products", icon: Package },
  orders: { labelKey: "dashboard.shop.tabs.orders", icon: Receipt },
  refunds: { labelKey: "dashboard.shop.tabs.refunds", icon: RotateCcw },
};

function buildQueryString(props: ShopSectionProps): string {
  const params = new URLSearchParams();
  if (props.forClientUserId) params.set("forUserId", props.forClientUserId);
  if (props.customRange) {
    params.set("since", props.customRange.since);
    params.set("until", props.customRange.until);
  } else {
    params.set("datePreset", props.datePreset);
  }
  return params.toString();
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(locale);
}

/**
 * Fetch produktów/zamówień to wielostronicowe, throttlowane odpytanie Shopify —
 * ponawianie go trzy razy (domyślka react-query) tylko dokłada obciążenia, a na
 * 401/403 (user spoza whitelisty Shopify) nie ma czego ponawiać.
 */
function retryExceptAuth(failureCount: number, error: Error): boolean {
  if (error.message.startsWith("401:") || error.message.startsWith("403:")) return false;
  return failureCount < 1;
}

export default function ShopSection({
  datePreset,
  customRange,
  forClientUserId,
}: ShopSectionProps) {
  const { t, i18n } = useTranslation();
  const [subView, setSubView] = useState<ShopSubView>("products");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const windowQs = buildQueryString({ datePreset, customRange, forClientUserId });
  const productsQs = forClientUserId ? `?forUserId=${forClientUserId}` : "";

  const productsQuery = useQuery<ShopProductsResponse>({
    queryKey: ["shopify-shop-products", forClientUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/shopify/shop/products${productsQs}`),
    staleTime: STALE_TIME_MS,
    retry: retryExceptAuth,
  });

  const ordersQuery = useQuery<ShopOrdersResponse>({
    queryKey: ["shopify-shop-orders", windowQs],
    queryFn: () => apiRequest("GET", `/api/integrations/shopify/shop/orders?${windowQs}`),
    staleTime: STALE_TIME_MS,
    retry: retryExceptAuth,
  });

  const refundsQuery = useQuery<{ refunds: ShopRefundRow[] }>({
    queryKey: ["shopify-shop-refunds", windowQs],
    queryFn: () => apiRequest("GET", `/api/integrations/shopify/shop/refunds?${windowQs}`),
    staleTime: STALE_TIME_MS,
    retry: retryExceptAuth,
  });

  const connected = productsQuery.data?.connected ?? ordersQuery.data?.connected ?? true;
  const currency = ordersQuery.data?.currency || DEFAULT_CURRENCY;
  const lowStockThreshold = productsQuery.data?.lowStockThreshold ?? 5;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (subView === "products") {
      const rows = productsQuery.data?.products ?? [];
      return needle
        ? rows.filter(
            (r) => r.title.toLowerCase().includes(needle) || r.sku.toLowerCase().includes(needle)
          )
        : rows;
    }
    if (subView === "orders") {
      const rows = ordersQuery.data?.orders ?? [];
      return needle
        ? rows.filter(
            (r) =>
              r.name.toLowerCase().includes(needle) ||
              (r.customer ?? "").toLowerCase().includes(needle)
          )
        : rows;
    }
    const rows = refundsQuery.data?.refunds ?? [];
    return needle ? rows.filter((r) => (r.orderId ?? "").toLowerCase().includes(needle)) : rows;
  }, [subView, search, productsQuery.data, ordersQuery.data, refundsQuery.data]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  function switchSubView(next: ShopSubView) {
    setSubView(next);
    setSearch("");
    setPage(0);
  }

  const activeQuery =
    subView === "products" ? productsQuery : subView === "orders" ? ordersQuery : refundsQuery;

  if (!connected) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center space-y-3">
        <Store className="w-6 h-6 text-muted-foreground mx-auto" />
        <p className="text-sm text-muted-foreground">{t("dashboard.shop.emptyConnect")}</p>
        <Link href="/integracje">
          <Button variant="outline" size="sm">
            {t("dashboard.shop.emptyConnectCta")}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Store className="w-4 h-4 text-[#ef3e29]" />
        <h2 className="text-sm font-semibold text-foreground">{t("dashboard.shop.title")}</h2>
        {productsQuery.data?.shopName && (
          <span className="text-xs text-muted-foreground">{productsQuery.data.shopName}</span>
        )}
      </div>

      {/* KPI sklepu */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-dense md:gap-balanced">
        <DashboardMetricCard
          title={t("dashboard.shop.kpi.netRevenue")}
          value={formatCurrency(ordersQuery.data?.netRevenue ?? 0, currency)}
          icon={Wallet}
        />
        <DashboardMetricCard
          title={t("dashboard.shop.kpi.orders")}
          value={String(ordersQuery.data?.liveOrders ?? 0)}
          icon={ShoppingBag}
        />
        <DashboardMetricCard
          title={t("dashboard.shop.kpi.aov")}
          value={formatCurrency(ordersQuery.data?.avgOrderValue ?? 0, currency)}
          icon={Receipt}
        />
        <DashboardMetricCard
          title={t("dashboard.shop.kpi.activeProducts")}
          value={String(productsQuery.data?.activeProducts ?? 0)}
          icon={Package}
        />
      </div>

      {/* Shopify pokazuje aplikacji tylko ostatnie 60 dni zamówień (scope
          read_orders) — mówimy o tym wprost, zamiast udawać pusty wynik. */}
      {ordersQuery.data?.visibleSince && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/60 px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            {t("dashboard.shop.visibilityLimit", {
              date: formatDate(ordersQuery.data.visibleSince, i18n.language),
            })}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card">
        {/* Przełącznik list + wyszukiwarka */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border-b border-border">
          <div className="flex items-center gap-1 p-1 bg-muted border border-border rounded-lg w-full sm:w-fit">
            {(
              Object.entries(SUB_VIEW_CONFIG) as [
                ShopSubView,
                (typeof SUB_VIEW_CONFIG)[ShopSubView],
              ][]
            ).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => switchSubView(key)}
                  className={`flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                    subView === key
                      ? "bg-[#ef3e29] text-foreground"
                      : "text-muted-foreground hover:text-foreground bg-accent hover:bg-[#1f2330]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t(config.labelKey)}
                </button>
              );
            })}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={t(`dashboard.shop.searchPlaceholder.${subView}`)}
              className="pl-9 h-9 text-sm"
            />
          </div>
        </div>

        {activeQuery.isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : activeQuery.isError ? (
          <div className="p-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">{t("dashboard.shop.error")}</p>
            <Button variant="outline" size="sm" onClick={() => activeQuery.refetch()}>
              {t("dashboard.shop.retry")}
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {t(`dashboard.shop.empty.${subView}`)}
          </p>
        ) : (
          <div className="overflow-x-auto">
            {subView === "products" && (
              <ProductsTable
                rows={pageRows as ShopProductRow[]}
                currency={currency}
                lowStockThreshold={lowStockThreshold}
              />
            )}
            {subView === "orders" && <OrdersTable rows={pageRows as ShopOrderRow[]} />}
            {subView === "refunds" && (
              <RefundsTable rows={pageRows as ShopRefundRow[]} fallbackCurrency={currency} />
            )}
          </div>
        )}

        {pageCount > 1 && (
          <div className="flex items-center justify-end gap-2 p-3 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {currentPage + 1} / {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryBadge({ inventory, threshold }: { inventory: number; threshold: number }) {
  const { t } = useTranslation();
  // Progi identyczne z alertami monitoringu (buildInventoryAlerts): 0 = wyprzedany,
  // <= próg = niski stan (próg inkluzywny).
  if (inventory <= 0) return <Badge variant="destructive">{t("dashboard.shop.stock.out")}</Badge>;
  if (inventory <= threshold)
    return (
      <Badge className="bg-amber-500/15 text-amber-400 hover:bg-amber-500/15">
        {inventory} · {t("dashboard.shop.stock.low")}
      </Badge>
    );
  return <span className="text-sm text-foreground">{inventory}</span>;
}

function ProductsTable({
  rows,
  currency,
  lowStockThreshold,
}: {
  rows: ShopProductRow[];
  currency: string;
  lowStockThreshold: number;
}) {
  const { t } = useTranslation();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("dashboard.shop.columns.product")}</TableHead>
          <TableHead>{t("dashboard.shop.columns.sku")}</TableHead>
          <TableHead className="text-right">{t("dashboard.shop.columns.price")}</TableHead>
          <TableHead className="text-right">{t("dashboard.shop.columns.inventory")}</TableHead>
          <TableHead>{t("dashboard.shop.columns.status")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-medium">
              {row.title}
              {row.variantCount > 1 && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {t("dashboard.shop.variants", { count: row.variantCount })}
                </span>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground">{row.sku || "—"}</TableCell>
            <TableCell className="text-right">
              {formatCurrency(parseFloat(row.price) || 0, currency)}
            </TableCell>
            <TableCell className="text-right">
              <InventoryBadge inventory={row.inventory} threshold={lowStockThreshold} />
            </TableCell>
            <TableCell className="text-muted-foreground">{row.status}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function OrdersTable({ rows }: { rows: ShopOrderRow[] }) {
  const { t, i18n } = useTranslation();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("dashboard.shop.columns.order")}</TableHead>
          <TableHead>{t("dashboard.shop.columns.date")}</TableHead>
          <TableHead>{t("dashboard.shop.columns.customer")}</TableHead>
          <TableHead className="text-right">{t("dashboard.shop.columns.value")}</TableHead>
          <TableHead>{t("dashboard.shop.columns.paymentStatus")}</TableHead>
          <TableHead className="text-right">{t("dashboard.shop.columns.items")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-medium">{row.name}</TableCell>
            <TableCell className="text-muted-foreground">
              {formatDate(row.createdAt, i18n.language)}
            </TableCell>
            <TableCell className="text-muted-foreground">{row.customer || "—"}</TableCell>
            <TableCell className="text-right">{formatCurrency(row.total, row.currency)}</TableCell>
            <TableCell>
              {row.cancelled ? (
                <Badge variant="destructive">{t("dashboard.shop.orderCancelled")}</Badge>
              ) : (
                <span className="text-sm text-muted-foreground">{row.financialStatus}</span>
              )}
            </TableCell>
            <TableCell className="text-right">{row.itemCount}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RefundsTable({
  rows,
  fallbackCurrency,
}: {
  rows: ShopRefundRow[];
  fallbackCurrency: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("dashboard.shop.columns.order")}</TableHead>
          <TableHead>{t("dashboard.shop.columns.refundDate")}</TableHead>
          <TableHead className="text-right">{t("dashboard.shop.columns.amount")}</TableHead>
          <TableHead>{t("dashboard.shop.columns.reason")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-medium">{row.orderId || "—"}</TableCell>
            <TableCell className="text-muted-foreground">
              {formatDate(row.refundedAt, i18n.language)}
            </TableCell>
            <TableCell className="text-right text-red-400">
              {formatCurrency(row.amount, row.currency || fallbackCurrency)}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {t(`dashboard.shop.refundReason.${row.reason}`, {
                defaultValue: row.reason,
              })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
